export interface QueueItem {
    episode: Episode;
    podcast: Podcast;
}

/** How long the next episode is announced before it starts, so there is time to say no */
export const ADVANCE_DELAY_MS = 5000;

/**
 * The episode to play after `endedId`: the first one further down the saved list that has not been played.
 * Nothing when the episode that ended is not in the list (the listener was not going through it) or is last.
 */
export const nextToPlay = (
    items: readonly QueueItem[],
    endedId: number,
    isPlayed: (episodeId: number) => boolean
): QueueItem | undefined => {
    const index = items.findIndex((item) => item.episode.id === endedId);
    if (index === -1) return undefined;
    return items.slice(index + 1).find((item) => !isPlayed(item.episode.id));
};

export interface AutoAdvanceDeps {
    /** The saved list, in order */
    queue: () => readonly QueueItem[];
    isPlayed: (episodeId: number) => boolean;
    removeFromQueue: (episode: Episode) => void;
    /** Whether the listener wants the next episode started; on when not given */
    enabled?: () => boolean;
    /** True while the episode that ended is still what is loaded and nothing is playing: the listener has not moved on */
    stillIdleOn: (episodeId: number) => boolean;
    start: (item: QueueItem) => void;
    /** Tell the listener what comes next and offer `cancel`; returns the way to take the notice down again */
    announce: (item: QueueItem, cancel: () => void) => () => void;
    delayMs?: number;
}

/**
 * Plays on through the saved list when an episode ends: the finished episode leaves the list, the next
 * unplayed one is announced and started after a few seconds unless the listener cancels or moves on.
 */
export class AutoAdvance {
    private timer: ReturnType<typeof setTimeout> | undefined;
    private dismiss: (() => void) | undefined;

    constructor(private readonly deps: AutoAdvanceDeps) {}

    /** `stopAfterEpisode`: a sleep timer set to end with this episode wants silence, not the next one */
    onEnded(endedId: number, { stopAfterEpisode = false }: { stopAfterEpisode?: boolean } = {}) {
        this.cancel();

        const next = nextToPlay(this.deps.queue(), endedId, this.deps.isPlayed);
        const ended = this.deps.queue().find((item) => item.episode.id === endedId);
        if (ended) this.deps.removeFromQueue(ended.episode);
        if (!next || stopAfterEpisode || this.deps.enabled?.() === false) return;

        this.dismiss = this.deps.announce(next, () => this.cancel());
        this.timer = setTimeout(() => {
            this.cancel();
            if (this.deps.stillIdleOn(endedId)) this.deps.start(next);
        }, this.deps.delayMs ?? ADVANCE_DELAY_MS);
    }

    /** Drop a pending start, e.g. when the listener says no */
    cancel() {
        clearTimeout(this.timer);
        this.timer = undefined;
        this.dismiss?.();
        this.dismiss = undefined;
    }
}
